import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/common/Layout';
import { bookService } from '../../services/bookService';
import { useAuth } from '../../context/AuthContext';
import { 
  BookOpenIcon, 
  UsersIcon, 
  TagIcon, 
  ChartBarIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface DashboardStats {
  totalBooks: number;
  totalUsers: number;
  totalCategories: number;
  recentBooks: any[];
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalBooks: 0,
    totalUsers: 0,
    totalCategories: 0,
    recentBooks: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const books = await bookService.getBooks();
        const recentBooks = books.slice(0, 5); // Get 5 most recent books
        
        setStats({
          totalBooks: books.length,
          totalUsers: 0, // TODO: Implement user count
          totalCategories: 0, // TODO: Implement category count
          recentBooks
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statCards = [
    {
      name: 'Total Books',
      value: stats.totalBooks,
      icon: BookOpenIcon,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      name: 'Categories',
      value: stats.totalCategories,
      icon: TagIcon,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      name: 'Users',
      value: stats.totalUsers,
      icon: UsersIcon,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      name: 'Analytics',
      value: 'View',
      icon: ChartBarIcon,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Page header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">
                Welcome back, {user?.displayName?.split(' ')[0] || 'User'}! 👋
              </h1>
              <p className="mt-2 text-blue-100">
                Ready to manage your book collection today?
              </p>
            </div>
            <div className="hidden md:block">
              <BookOpenIcon className="h-16 w-16 text-blue-200" />
            </div>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={stat.name}
                className="relative bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-center">
                  <div className={`${stat.bgColor} rounded-xl p-3`}>
                    <IconComponent className={`h-7 w-7 ${stat.textColor}`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      {stat.name}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {stat.value}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent books */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-100">
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Recent Books
            </h3>
            {stats.recentBooks.length > 0 ? (
              <div className="space-y-4">
                {stats.recentBooks.map((book) => (
                  <div
                    key={book.id}
                    className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                  >
                    {book.coverImageUrl ? (
                      <img
                        src={book.coverImageUrl}
                        alt={book.title}
                        className="h-14 w-10 object-cover rounded-lg shadow-sm"
                      />
                    ) : (
                      <div className="h-14 w-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
                        <BookOpenIcon className="h-6 w-6 text-blue-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {book.title}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        by {book.author}
                      </p>
                      <div className="flex items-center mt-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          {book.genre}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {new Date(book.createdAt.seconds * 1000).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="mx-auto h-20 w-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                  <BookOpenIcon className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">No books yet</h3>
                <p className="mt-2 text-gray-600">
                  Get started by adding your first book to the collection.
                </p>
                <div className="mt-6">
                  <Link
                    to="/add-book"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    Add Your First Book
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-100">
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Link
                to="/add-book"
                className="group flex items-center justify-center px-6 py-4 border-2 border-blue-200 text-sm font-semibold rounded-xl text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                <PlusIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                Add New Book
              </Link>
              <Link
                to="/books"
                className="group flex items-center justify-center px-6 py-4 border-2 border-gray-200 text-sm font-semibold rounded-xl text-gray-700 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
              >
                <BookOpenIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                View All Books
              </Link>
              <Link
                to="/analytics"
                className="group flex items-center justify-center px-6 py-4 border-2 border-purple-200 text-sm font-semibold rounded-xl text-purple-700 bg-purple-50 hover:bg-purple-100 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
              >
                <ChartBarIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform duration-200" />
                Analytics
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard; 